import { coordKey } from "../hex-path/hex-grid";
import type { SeededRng } from "../hex-path/seeded-rng";
import type { HexCoord } from "../hex-path/types";
import {
	type EnvironmentFootprint,
	type EnvironmentFootprintPlacementContext,
	isValidEnvironmentFootprintPlacement,
	listFootprintAnchorCandidates,
	occupyFootprintCells,
	resolveEnvironmentFootprint,
} from "./environment-footprint";

export type { EnvironmentFootprint } from "./environment-footprint";

export interface EnvironmentImageConfig {
	src: string;
	count?: number;
	minCount?: number;
	maxCount?: number;
	/** Rectangular tile footprint anchored at top-left; defaults to 1×1. */
	footprint?: Partial<EnvironmentFootprint>;
	/** Optional width/height aspect ratio for sprite rendering (defaults to footprint box). */
	aspectRatio?: number;
	minDistanceToPath?: number;
	maxDistanceToPath?: number;
	minRow?: number;
	maxRow?: number;
	minCol?: number;
	maxCol?: number;
	minSpacing?: number;
}

export interface PlacedEnvironmentImage {
	src: string;
	row: number;
	col: number;
	footprintWidth: number;
	footprintHeight: number;
	aspectRatio?: number;
	flipHorizontal?: boolean;
}

export function placeEnvironmentImages(params: {
	configs: EnvironmentImageConfig[];
	rows: number;
	cols: number;
	occupied: Set<string>;
	rng: SeededRng;
	maxPlacements?: number;
}): PlacedEnvironmentImage[] {
	const placements: PlacedEnvironmentImage[] = [];
	const occupied = new Set(params.occupied);
	const maxPlacements =
		params.maxPlacements ??
		Math.max(
			12,
			params.configs.reduce((sum, config) => {
				return (
					sum +
					(config.maxCount ?? config.minCount ?? config.count ?? 1)
				);
			}, 0),
		);
	const pathCells = [...params.occupied].map((key) => {
		const [row, col] = key.split(",").map(Number);
		return { row: row ?? 0, col: col ?? 0 };
	});
	const placedBySrc = new Map<
		string,
		Array<{
			anchor: { row: number; col: number };
			footprint: EnvironmentFootprint;
			minSpacing: number;
		}>
	>();
	const allPlaced: Array<{
		anchor: { row: number; col: number };
		footprint: EnvironmentFootprint;
		minSpacing: number;
	}> = [];

	for (const config of params.configs) {
		const footprint = resolveEnvironmentFootprint(config.footprint);
		const minRow = Math.max(0, config.minRow ?? 0);
		const maxRow = Math.min(
			params.rows - 1,
			config.maxRow ?? params.rows - 1,
		);
		const minCol = Math.max(0, config.minCol ?? 0);
		const maxCol = Math.min(
			params.cols - 1,
			config.maxCol ?? params.cols - 1,
		);
		const minDistanceToPath = config.minDistanceToPath ?? 0;
		const maxDistanceToPath =
			config.maxDistanceToPath ?? Number.POSITIVE_INFINITY;
		const minSpacing = config.minSpacing ?? 0;
		const count =
			config.minCount !== undefined && config.maxCount !== undefined
				? config.minCount +
					params.rng.int(config.maxCount - config.minCount + 1)
				: (config.count ?? 1);
		const candidates = listFootprintAnchorCandidates(footprint, {
			minRow,
			maxRow,
			minCol,
			maxCol,
		});
		const shuffled = [...candidates];
		for (let index = shuffled.length - 1; index > 0; index -= 1) {
			const swapIndex = params.rng.int(index + 1);
			const current = shuffled[index];
			if (!current) {
				continue;
			}
			const swapCandidate = shuffled[swapIndex] ?? current;
			shuffled[index] = swapCandidate;
			shuffled[swapIndex] = current;
		}
		const alreadyPlaced = placedBySrc.get(config.src) ?? [];
		let placed = 0;
		for (const anchor of shuffled) {
			if (placed >= count) {
				break;
			}
			const placementContext: EnvironmentFootprintPlacementContext = {
				rows: params.rows,
				cols: params.cols,
				occupied,
				pathCells,
				minRow,
				maxRow,
				minCol,
				maxCol,
				minDistanceToPath,
				maxDistanceToPath,
				minSpacing,
				existingPlacements: [
					// Same-kind placements must respect this config's minSpacing (existing behavior).
					...alreadyPlaced.map((p) => ({
						anchor: p.anchor,
						footprint: p.footprint,
					})),
					// Cross-kind placements: use the *stricter* of the two configs' minSpacing.
					...allPlaced
						.filter((p) => Math.max(minSpacing, p.minSpacing) > 0)
						.map((p) => ({
							anchor: p.anchor,
							footprint: p.footprint,
						})),
				],
			};
			if (
				!isValidEnvironmentFootprintPlacement(
					anchor,
					footprint,
					placementContext,
				)
			) {
				continue;
			}
			occupyFootprintCells(anchor, footprint, occupied);
			placements.push({
				src: config.src,
				row: anchor.row,
				col: anchor.col,
				footprintWidth: footprint.width,
				footprintHeight: footprint.height,
				aspectRatio: config.aspectRatio,
				flipHorizontal: params.rng.next() < 0.5,
			});
			alreadyPlaced.push({ anchor, footprint, minSpacing });
			allPlaced.push({ anchor, footprint, minSpacing });
			placed += 1;
			if (placements.length >= maxPlacements) {
				return placements;
			}
		}
		placedBySrc.set(config.src, alreadyPlaced);
	}

	return placements;
}

export function occupiedCellsFromPath(path: HexCoord[]): Set<string> {
	return new Set(path.map((c) => coordKey(c.row, c.col)));
}
